import React, {useCallback, useMemo} from 'react';
import {
  Box,
  Grid,
  GridCol,
  Heading,
  spaceHelpers
} from '@flixbus/honeycomb-react';
import DriverCostTable from '@/components/DriverCosts/DriverCostsTable/DriverCostsTable';
import {useUpdateCountryDriverCostsMutation} from '@/Pages/RegionalCosts/hooks/useUpdateCountryDriverCostsMutation';
import RegionalCostsDriverCostHelpBox from '../RegionalCostsDriverCostHelpBox/RegionalCostsDriverCostHelpBox';
import RegionalCostsDriverConceptTypes from '../RegionalCostsDriverConceptTypes/RegionalCostsDriverConceptTypes';
import {RegionalCostsDriverCostContentProps} from './RegionalCostsDriverCostContent.types';
import styles from './RegionalCostsDriverCostContent.module.scss';
import {getRegionalCostsDriverCostTableConfig} from './RegionalCostsDriverCostContent.data';
import {useLocalUnitsContext} from '@/Pages/Common/LocalUnits';
import {RegionalCostsDriverCost} from '@/Pages/RegionalCosts/store/types';

const RegionalCostsDriverCostContent: React.FC<
  RegionalCostsDriverCostContentProps
> = ({country, year, driverCost}) => {
  const {5: space5} = spaceHelpers();

  const {localUnits} = useLocalUnitsContext();

  const {updateCountryDriverCosts} = useUpdateCountryDriverCostsMutation();
  const updateDriverCosts = useCallback(
    async (updatedCosts: RegionalCostsDriverCost) => {
      await updateCountryDriverCosts(updatedCosts, country.id, year);
    },
    [country.id, updateCountryDriverCosts, year]
  );

  const driverCostsConfig = useMemo(
    () => getRegionalCostsDriverCostTableConfig(country, localUnits),
    [localUnits, country]
  );

  return (
    <>
      <div className={styles.header}>
        <Heading size={3}>
          Default cost for {country.name} ({year})
        </Heading>
        <div className={styles.subtitle}>
          Here you can define country cost factor defaults.They will be used
          when you cannot provide cost factors for a certain bus partner.{' '}
        </div>
        <div className={styles.subtitle}>
          Fields marked with an * are mandatory and values must be specified at
          either regional or partner level.
        </div>
      </div>

      <Box>
        <Grid extraClasses="gap-12">
          <GridCol>
            <DriverCostTable
              country={country}
              year={year}
              config={driverCostsConfig}
              driverCost={driverCost}
              updateFn={updateDriverCosts}
            />
          </GridCol>

          <GridCol>
            <RegionalCostsDriverCostHelpBox />
          </GridCol>
        </Grid>
      </Box>

      <Box extraClasses={space5.top}>
        <RegionalCostsDriverConceptTypes />
      </Box>
    </>
  );
};

export default React.memo(RegionalCostsDriverCostContent);
